import { http, request, ApiError } from "@/api/client";
import type { ApiItemResponse, SessionPayload } from "@/api/types";
import type { PermissionMatrix, Role, RoleCode } from "@/types/domain";
import { hashPassword } from "@/lib/session-crypto";
import { clearAuthState, setAuthState } from "@/state/auth";

export interface VerifySessionResponse {
  valid: boolean;
  user_id?: string;
}

type RawSessionData = {
  currentRole?: string;
  current_role?: string;
  role?: Role | null;
  permissions?: PermissionMatrix | null;
  session_user_id?: string;
  session_value?: string;
};

function normalizeSessionPayload(raw: RawSessionData | undefined | null): SessionPayload {
  const currentRole = (raw?.currentRole || raw?.current_role || "sdk_admin") as RoleCode | string;
  return {
    currentRole,
    role: (raw?.role || null) as Role,
    permissions: (raw?.permissions || null) as PermissionMatrix,
  };
}

function setClientSessionCookies(userId: string, value: string) {
  if (typeof document === "undefined") return;
  // Local non-HttpOnly cookies so MSW can restore mock session after real login / reload.
  document.cookie = `session_user_id=${encodeURIComponent(userId)}; path=/; SameSite=Lax`;
  document.cookie = `session_value=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
}

function clearClientSessionCookies() {
  if (typeof document === "undefined") return;
  document.cookie = "session_user_id=; path=/; Max-Age=0";
  document.cookie = "session_value=; path=/; Max-Age=0";
}

export const sessionApi = {
  /**
   * POST /api/session/create — aligned with client_nodejs:
   * body { user_id, user_pwd } where user_pwd = MD5(plaintext).
   */
  login: async (userId: string, password: string, remember = true) => {
    const uid = userId.trim();
    const user_pwd = hashPassword(password);
    const res = await request<{ data: RawSessionData }>("POST", "/api/session/create", {
      body: { user_id: uid, user_pwd },
    });
    const data = normalizeSessionPayload(res.data);
    setAuthState(uid, remember);
    // Seed local cookies for MSW-backed get/verify/role after real login.
    const sessionValue = res.data?.session_value || `web_${Date.now().toString(36)}`;
    setClientSessionCookies(uid, sessionValue);
    return { data } satisfies ApiItemResponse<SessionPayload>;
  },

  /** GET /api/session/verify — mock auth (real backend not used for this route). */
  verify: () => http.get<VerifySessionResponse>("/api/session/verify"),

  /** GET /api/session — mock (same shape as client_nodejs getSession). */
  get: async () => {
    const res = await http.get<{ data: RawSessionData }>("/api/session");
    return { data: normalizeSessionPayload(res.data) } satisfies ApiItemResponse<SessionPayload>;
  },

  /** POST /api/session/role — mock (same shape as client_nodejs switchRole). */
  setRole: (role: string) => http.post<ApiItemResponse<{ currentRole: string }>>("/api/session/role", { role }),

  me: () => http.get<ApiItemResponse<Role>>("/api/me"),

  logout: async () => {
    try {
      await http.post("/api/session/logout", {});
    } catch {
      // ignore — clear local state anyway
    }
    clearClientSessionCookies();
    clearAuthState();
  },
};

export { ApiError };
